import React, { useCallback, useEffect, useState } from "react";
import {
  ChartBarIcon,
  CheckCircleIcon,
  XCircleIcon,
  ChartPieIcon,
  ArrowPathIcon,
  TagIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import { useSoundChartTracking } from "../../../hooks/useSoundChartTracking";
import { adminApi } from "../../../repositories/admin";
import Pagination from "../../../components/common/Pagination";
import AimsTracking from "./AimsTracking";
import PlaylistReadPathCard from "./PlaylistReadPathCard";
import PlaylistSyncSettingsCard from "./PlaylistSyncSettingsCard";
import DataTable, { DataTableColumn, dataTableRowClass, dataTableThClass, dataTableTdClass } from "../../../components/ui/DataTable";
import { Skeleton, SkeletonTableRows } from "../../../components/ui/skeleton";
import { cn } from "../../../DashboardPage/lib/utils";
import Button from "../../../components/ui/Button";

type ActiveView = "cards" | "soundchart" | "aims";

/** Current-month SoundCharts consumption, from GET /admin/soundcharts-quota. */
interface SoundchartsQuota {
  monthKey: string;
  /**
   * Where totalCalls and quota come from: "soundcharts" is the account's own usage, every
   * server included; "app" is this server's count, used when SoundCharts can't be read.
   */
  source: "soundcharts" | "app";
  /** This server's playlist sync calls, from sync_runs. */
  syncCalls: number;
  /** This server's other SoundCharts calls, from the tracking rollup. Charged the same. */
  trackedCalls: number;
  totalCalls: number;
  /** The limit SoundCharts reports for the plan. */
  quota: number;
  percentUsed: number;
  remaining: number;
  /** At 100% every playlist sync is failed until the month resets. */
  exhausted: boolean;
  /** Percentages at which admins are emailed; the bar changes colour at the same points. */
  alertThresholds: number[];
}

const SoundChartTracking: React.FC = () => {
  const { analytics, loading, error, fetchAnalytics } = useSoundChartTracking();
  const [selectedMonths, setSelectedMonths] = useState<number>(3);
  const [activeView, setActiveView] = useState<ActiveView>("cards");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(100);
  const [quota, setQuota] = useState<SoundchartsQuota | null>(null);

  useEffect(() => {
    if (activeView === "soundchart") {
      fetchAnalytics(selectedMonths, true, currentPage, pageSize);
    }
  }, [fetchAnalytics, selectedMonths, activeView, currentPage, pageSize]);

  // The quota is one number for the calendar month, so it follows neither the months selector
  // nor the table's paging. A failure leaves the card out rather than breaking the page.
  const loadQuota = useCallback(async () => {
    try {
      const res = await adminApi.getSoundchartsQuota();
      setQuota(res?.data?.data ?? null);
    } catch (err) {
      console.error("Failed to load SoundCharts quota:", err);
      setQuota(null);
    }
  }, []);

  useEffect(() => {
    if (activeView === "soundchart") loadQuota();
  }, [activeView, loadQuota]);

  const handleRefresh = () => {
    fetchAnalytics(selectedMonths, true, currentPage, pageSize);
    loadQuota();
  };

  const handleMonthsChange = (months: number) => {
    setSelectedMonths(months);
    setCurrentPage(1); // Reset to first page when changing months
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  const handleBack = () => {
    setActiveView("cards");
    setCurrentPage(1);
  };

  // AIMS detail view
  if (activeView === "aims") {
    return <AimsTracking onBack={handleBack} />;
  }

  // Card grid view
  if (activeView === "cards") {
    return (
      <div>
        <h3 className="mb-4 text-lg font-semibold text-black">
          Third-Party API Services
        </h3>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {/* SoundChart Card */}
          <button
            onClick={() => setActiveView("soundchart")}
            className="p-6 text-left transition-colors bg-white border border-neutral-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 focus:outline-none focus-visible:border-primary-700"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-lg bg-primary-50">
                  <ChartBarIcon className="w-6 h-6 text-primary-700" />
                </div>
                <h4 className="text-lg font-semibold text-neutral-900">SoundChart</h4>
              </div>
              <svg className="w-5 h-5 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
            <p className="text-sm text-neutral-600">
              View SoundChart API usage, track success rates, and monitor API calls
            </p>
          </button>

          {/* AIMS Card */}
          <button
            onClick={() => setActiveView("aims")}
            className="p-6 text-left transition-colors bg-white border border-neutral-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 focus:outline-none focus-visible:border-primary-700"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-lg bg-primary-50">
                  <TagIcon className="w-6 h-6 text-primary-700" />
                </div>
                <h4 className="text-lg font-semibold text-neutral-900">AIMS</h4>
              </div>
              <svg className="w-5 h-5 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
            <p className="text-sm text-neutral-600">
              View AIMS auto-tagging API usage, track success rates, and monitor API calls
            </p>
          </button>
        </div>
      </div>
    );
  }

  // Show detail view if expanded
  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="w-56 h-6" />
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3" aria-busy="true">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="p-4 bg-white border rounded-lg border-neutral-200">
              <div className="flex items-center justify-between">
                <div className="space-y-2">
                  <Skeleton className="w-28 h-3" />
                  <Skeleton className="w-16 h-6" />
                </div>
                <Skeleton className="w-10 h-10 rounded-full" />
              </div>
            </div>
          ))}
        </div>
        <div className="overflow-hidden bg-white border rounded-lg border-neutral-200">
          <table className="min-w-full">
            <tbody>
              <SkeletonTableRows rows={8} columns={6} />
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <button
          onClick={handleBack}
          className="inline-flex items-center gap-2 mb-4 text-sm font-medium transition-colors border border-transparent rounded-md text-neutral-600 hover:text-primary-700 focus:outline-none focus-visible:border-primary-700"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Back to API Services
        </button>
        <div className="p-4 text-danger-600 border border-danger-200 rounded-lg bg-danger-50">
          <p className="font-semibold">Error loading analytics</p>
          <p className="text-sm">{error}</p>
          <Button variant="secondary" onClick={handleRefresh} className="mt-2">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  const summary = analytics?.summary;
  const trackingData = analytics?.data || [];

  // Header markup comes from DataTable's `head` prop (sortable headers and
  // multi-line cells); this list only sets the column count for its
  // loading and empty state rows.
  const apiCallColumns: DataTableColumn<any>[] = [
    { key: "apiCall", header: "" },
    { key: "date", header: "" },
    { key: "time", header: "" },
    { key: "userEmail", header: "" },
    { key: "company", header: "" },
    { key: "status", header: "" },
  ];
  return (
    <div>
      {/* Back Button */}
      <button
        onClick={handleBack}
        className="inline-flex items-center gap-2 mb-4 text-sm font-medium transition-colors border border-transparent rounded-md text-neutral-600 hover:text-primary-700 focus:outline-none focus-visible:border-primary-700"
      >
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 19l-7-7 7-7"
          />
        </svg>
        Back to API Services
      </button>

      {/* Header with Title */}
      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 rounded-lg bg-primary-50">
          <ChartBarIcon className="w-6 h-6 text-primary-700" />
        </div>
        <h3 className="text-lg font-semibold text-black">SoundChart API Analytics</h3>
      </div>

      {/* Header with Controls */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          {/* Month Selector */}
          <div className="relative">
            <select
              value={selectedMonths}
              onChange={(e) => handleMonthsChange(Number(e.target.value))}
              className="py-2 pl-3 pr-9 text-sm font-medium text-black transition-colors bg-white border rounded-lg shadow-sm cursor-pointer appearance-none border-neutral-300 hover:border-primary-500 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
            >
              <option value={1}>Last 1 Month</option>
              <option value={3}>Last 3 Months</option>
              <option value={6}>Last 6 Months</option>
              <option value={12}>Last 12 Months</option>
            </select>
            <ChevronDownIcon className="absolute w-4 h-4 -translate-y-1/2 pointer-events-none right-3 top-1/2 text-neutral-500" />
          </div>

          {/* Refresh Button */}
          <Button variant="secondary" onClick={handleRefresh}>
            <ArrowPathIcon className="w-4 h-4" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="mb-6">
          <h3 className="mb-4 text-lg font-semibold text-black">
            Summary Stats
          </h3>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
            {/* Total Calls */}
            <div className="p-4 bg-white border rounded-lg border-neutral-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-neutral-500 sm:text-sm">
                    Total API Calls
                  </p>
                  <p className="mt-1 text-xl font-bold text-black sm:text-2xl">
                    {summary.totalCalls.toLocaleString()}
                  </p>
                </div>
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-full shrink-0 bg-primary-50 text-primary-700">
                  <ChartBarIcon className="w-5 h-5" />
                </span>
              </div>
            </div>

            {/* Success Rate */}
            <div className="p-4 bg-white border rounded-lg border-neutral-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-neutral-500 sm:text-sm">
                    Success Rate
                  </p>
                  <p className="mt-1 text-xl font-semibold sm:text-2xl">
                    {summary.successRate}
                  </p>
                </div>
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-full shrink-0 bg-primary-50 text-primary-700">
                  <ChartPieIcon className="w-5 h-5" />
                </span>
              </div>
            </div>

            {/* Successful Calls */}
            <div className="p-4 bg-white border rounded-lg border-neutral-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-neutral-500 sm:text-sm">
                    Successful Calls
                  </p>
                  <p className="mt-1 text-xl font-bold text-black sm:text-2xl">
                    {summary.successfulCalls.toLocaleString()}
                  </p>
                </div>
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-full shrink-0 bg-success-50 text-success-700">
                  <CheckCircleIcon className="w-5 h-5" />
                </span>
              </div>
            </div>

            {/* Failed Calls */}
            <div className="p-4 bg-white border rounded-lg border-neutral-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-neutral-500 sm:text-sm">
                    Failed Calls
                  </p>
                  <p className="mt-1 text-xl font-bold text-black sm:text-2xl">
                    {summary.failedCalls.toLocaleString()}
                  </p>
                </div>
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-full shrink-0 bg-danger-50 text-danger-600">
                  <XCircleIcon className="w-5 h-5" />
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Apart from the cards above because it measures something else: they count a rolling
          window of tracked calls, this counts the calendar month the quota resets on, playlist
          syncs included. */}
      {quota && (
        <div className="p-4 mb-6 bg-white border rounded-lg border-neutral-200">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <p className="text-xs text-neutral-500 sm:text-sm">
              SoundCharts Monthly Quota · {quota.monthKey}
            </p>
            <p className="text-sm text-neutral-500">{quota.remaining.toLocaleString()} remaining</p>
          </div>

          <p className="mt-1 text-xl font-bold text-black sm:text-2xl">
            {quota.totalCalls.toLocaleString()}
            <span className="ml-1 text-base font-normal text-neutral-500">
              / {quota.quota.toLocaleString()} ({quota.percentUsed}%)
            </span>
          </p>

          <div className="w-full h-2 mt-3 overflow-hidden rounded-full bg-neutral-100">
            <div
              className={cn(
                "h-full rounded-full transition-all",
                quota.percentUsed >= 80
                  ? "bg-red-500"
                  : quota.percentUsed >= 50
                    ? "bg-amber-500"
                    : "bg-primary-600"
              )}
              // Capped at 100 so a spent quota fills the bar rather than overflowing it.
              style={{ width: `${Math.min(100, quota.percentUsed)}%` }}
            />
          </div>

          {quota.source === "app" ? (
            <p className="mt-2 text-xs font-medium text-amber-700">
              SoundCharts' usage could not be read, so this is this server's own count. Calls
              made from other servers are not included.
            </p>
          ) : (
            <p className="mt-2 text-xs text-neutral-500">
              SoundCharts' count for the whole account, every server included.
            </p>
          )}

          <p className="mt-1 text-xs text-neutral-500">
            This server: {quota.syncCalls.toLocaleString()} from playlist syncs,{" "}
            {quota.trackedCalls.toLocaleString()} from other calls. Admins are emailed at{" "}
            {quota.alertThresholds.join("%, ")}%.
          </p>

          {quota.exhausted && (
            <p className="mt-2 text-sm font-medium text-red-600">
              Quota spent: playlist syncs are paused until the plan renews on the 1st. Playlist
              data stays as it is but will not update.
            </p>
          )}
        </div>
      )}

      <PlaylistReadPathCard />
      <PlaylistSyncSettingsCard />

      {/* API Calls Detail Table */}
      <div className="mb-4">
        <h3 className="mb-4 text-lg font-semibold text-black">
          API Calls Detail
        </h3>
        <div className="overflow-x-auto bg-white border border-neutral-200 rounded-lg shadow-sm">
          {trackingData.length > 0 ? (
            <DataTable
              columns={apiCallColumns}
              loading={loading}
              skeletonRows={8}
              head={
                <thead>
                  <tr className="border-b border-neutral-200 bg-primary-50">
                    <th className={dataTableThClass}>API Call</th>
                    <th className={dataTableThClass}>Date</th>
                    <th className={dataTableThClass}>Time</th>
                    <th className={dataTableThClass}>User Email</th>
                    <th className={dataTableThClass}>Company</th>
                    <th className={dataTableThClass}>Status</th>
                  </tr>
                </thead>
              }
            >
                {trackingData.map((record) => (
                  <tr key={record.id} className={dataTableRowClass}>
                    <td className={cn(dataTableTdClass, "max-w-xs font-mono truncate")} title={record.api_call}>
                      {record.api_call}
                    </td>
                    <td className={cn(dataTableTdClass, "text-neutral-600")}>
                      {record.date}
                    </td>
                    <td className={cn(dataTableTdClass, "text-neutral-600")}>
                      {record.time}
                    </td>
                    <td className={cn(dataTableTdClass, "font-medium")}>
                      {record.email || record.userId || 'N/A'}
                    </td>
                    <td className={cn(dataTableTdClass, "text-neutral-600")}>
                      {record.labelName || record.labelId || 'N/A'}
                    </td>
                    <td className={dataTableTdClass}>
                      {record.isSucceeded ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 text-xs font-medium border rounded-full bg-success-50 text-success-800 border-success-200">
                          <CheckCircleIcon className="w-4 h-4" />
                          Success
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 text-xs font-medium border rounded-full bg-danger-50 text-danger-700 border-danger-200">
                          <XCircleIcon className="w-4 h-4" />
                          Failed
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
            </DataTable>
          ) : (
            <div className="p-8 text-center text-neutral-500">
              <ChartBarIcon className="w-12 h-12 mx-auto mb-2 text-neutral-400" />
              <p className="text-sm">
                No tracking data available for the selected period
              </p>
            </div>
          )}
        </div>

        {/* Pagination Controls */}
        {analytics?.pagination && trackingData.length > 0 && (
          <div className="flex flex-col gap-3 px-4 py-3 mt-4 bg-white border border-neutral-200 rounded-lg sm:flex-row sm:items-center sm:justify-between">
            {/* Page size selector - not covered by the shared Pagination component */}
            <div className="flex items-center gap-2 shrink-0">
              <label className="text-sm text-neutral-600">Show:</label>
              <div className="relative">
                <select
                  value={pageSize}
                  onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                  className="py-1 pl-2 pr-8 text-sm text-black transition-colors bg-white border rounded-md cursor-pointer appearance-none border-neutral-300 hover:border-primary-500 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                >
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={200}>200</option>
                  <option value={500}>500</option>
                </select>
                <ChevronDownIcon className="absolute w-4 h-4 -translate-y-1/2 pointer-events-none right-2 top-1/2 text-neutral-500" />
              </div>
              <span className="text-sm text-neutral-600">per page</span>
            </div>

            <div className="flex-grow">
              <Pagination
                page={currentPage}
                totalPages={analytics.pagination.totalPages}
                setPage={handlePageChange}
                totalItems={analytics.pagination.totalItems}
                limit={analytics.pagination.pageSize}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SoundChartTracking;
